/**
 * Dashboard page catalogs, per brand.
 *
 * The BFF accepts any slug matching `^[a-z0-9_-]+$`, but a page only does something if a real
 * dashboard screen answers to that slug. This catalog is that closed list, so the page form
 * offers a select instead of a free-text field.
 *
 * Mirrored by hand from each dashboard's own `src/BFF/types.ts` (`PageSlug`) and
 * `src/BFF/pages.config.ts` (`BFF_PAGE_SLUG_BY_ROUTE`). When a dashboard adds a BFF page,
 * add it here too — there is no build-time link between the two.
 *
 * Labels stay in English on purpose: they name screens of the dashboards themselves, which
 * are English products, even though the BFF Apps chrome around them is PT-BR.
 *
 * @module Shared/brand
 */

/** A dashboard the BFF serves content to. */
export type Brand = 'utilities' | 'dataoris' | 'sonarprint';

/** One selectable dashboard screen. */
export interface CatalogPage {
  /** BFF page slug — the key the BFF scopes What's New and tours by. */
  slug: string;
  /** Route the slug resolves to in that dashboard, shown as context under the select. */
  route: string;
  /** Screen name as the dashboard calls it. */
  label: string;
}

const UTILITIES_PAGES: CatalogPage[] = [
  { slug: 'projections', route: '/projections', label: 'Projections' },
  { slug: 'groups', route: '/groups', label: 'Groups' },
  { slug: 'coverage', route: '/network-coverage', label: 'Coverage map' },
  { slug: 'health-check', route: '/health-check', label: 'Health check' },
  /*
    The rewritten Health Check, behind its own flag in dashboard-utilities.

    Listed here ahead of the dashboard: the route exists there (`AUTH_ROUTES.DEVICES_MONITORING`)
    but it is not yet in that dashboard's `PageSlug` union nor in `BFF_PAGE_SLUG_BY_ROUTE`, so the
    dashboard does not ask the BFF for this slug yet. Content registered for it is stored and stays
    undelivered until those two entries land.
  */
  { slug: 'devices-monitoring', route: '/devices-monitoring', label: 'Device monitoring' },
  { slug: 'reports', route: '/reports', label: 'Reports' },
  { slug: 'alarms', route: '/alarms', label: 'Alarms' },
  { slug: 'alarms-occurrences', route: '/alarms/occurrences', label: 'Alarm occurrences' },
  { slug: 'alarms-severity', route: '/alarms/severity', label: 'Alarm severity' },
  /*
    Not a route of its own: the alarm configuration form is a state of `/alarms`, marked in the URL
    by `alarm_config=open`. The dashboard resolves it only through its tours registry, never through
    `BFF_PAGE_SLUG_BY_ROUTE`, so in practice this slug carries a tour and nothing else.
  */
  { slug: 'alarms-config', route: '/alarms?alarm_config=open', label: 'Alarm configuration' },
  { slug: 'device-list', route: '/devices', label: 'Device list' },
  { slug: 'tags', route: '/devices/tags', label: 'Tags' },
  { slug: 'my-account', route: '/settings/my-account', label: 'My account' },
  { slug: 'settings-overview', route: '/settings/overview', label: 'Settings overview' },
  { slug: 'settings-users', route: '/settings/users', label: 'Users' },
];

const DATAORIS_PAGES: CatalogPage[] = [
  { slug: 'inventory', route: '/inventory', label: 'Inventory' },
  { slug: 'dashboard', route: '/dashboard', label: 'Dashboard' },
  { slug: 'device-list', route: '/devices', label: 'Device list' },
  { slug: 'groups', route: '/groups', label: 'Groups' },
  { slug: 'reports', route: '/reports', label: 'Reports' },
  { slug: 'apps', route: '/apps', label: 'Apps' },
  { slug: 'push-notifications', route: '/notifications/push', label: 'Push notifications' },
  { slug: 'developer-sdk', route: '/developer/sdk', label: 'Developer SDK' },
  { slug: 'developer-webhook', route: '/developer/webhook', label: 'Developer webhook' },
  { slug: 'developer-batch', route: '/developer/batch', label: 'Developer batch' },
  {
    slug: 'developer-credentials',
    route: '/developer/credentials',
    label: 'Developer credentials',
  },
  { slug: 'settings-overview', route: '/settings/overview', label: 'Settings overview' },
  { slug: 'settings-users', route: '/settings/users', label: 'Users' },
  { slug: 'my-account', route: '/settings/my-account', label: 'My account' },
];

const SONARPRINT_PAGES: CatalogPage[] = [
  { slug: 'api', route: '/api', label: 'API' },
  { slug: 'reports', route: '/reports', label: 'Reports' },
  { slug: 'groups', route: '/groups', label: 'Groups' },
  { slug: 'device-blocks', route: '/device-blocks', label: 'Device blocks' },
  { slug: 'developer-sdk', route: '/developer/sdk', label: 'Developer SDK' },
  { slug: 'developer-batch', route: '/developer/batch', label: 'Developer batch' },
  {
    slug: 'developer-credentials',
    route: '/developer/credentials',
    label: 'Developer credentials',
  },
  { slug: 'settings-overview', route: '/settings/overview', label: 'Settings overview' },
  { slug: 'settings-users', route: '/settings/users', label: 'Users' },
  { slug: 'my-account', route: '/settings/my-account', label: 'My account' },
];

/** Every brand's catalog, keyed by brand. */
export const PAGE_CATALOG: Record<Brand, CatalogPage[]> = {
  utilities: UTILITIES_PAGES,
  dataoris: DATAORIS_PAGES,
  sonarprint: SONARPRINT_PAGES,
};

/**
 * Tenant slug → brand, for tenants whose `brand` field is not set.
 *
 * The BFF persists `brand` on the tenant, so {@link resolveBrand} prefers that. This map only
 * covers the four tenants that predate the field; anything else falls back to `utilities`.
 */
const BRAND_BY_TENANT: Record<string, Brand> = {
  'water-platform': 'utilities',
  'energy-platform': 'utilities',
  dataoris: 'dataoris',
  'sonar-print': 'sonarprint',
};

/** Default for a tenant this build has never heard of. */
export const FALLBACK_BRAND: Brand = 'utilities';

/** Resolves a brand from a tenant slug alone, for callers that only have the slug. */
export function getBrandForTenant(tenantSlug: string | null | undefined): Brand {
  if (!tenantSlug) return FALLBACK_BRAND;
  return BRAND_BY_TENANT[tenantSlug] ?? FALLBACK_BRAND;
}

/**
 * Resolves a tenant's brand, preferring the value the BFF stores.
 *
 * Falls back to the slug map for tenants created before the field existed, then to
 * {@link FALLBACK_BRAND}. Prefer this over {@link getBrandForTenant} wherever the tenant
 * record is at hand.
 */
export function resolveBrand(tenant: { slug?: string | null; brand?: Brand | null }): Brand {
  return tenant.brand ?? getBrandForTenant(tenant.slug);
}

/** The pages a tenant may point content at. */
export function getPagesForTenant(tenantSlug: string | null | undefined): CatalogPage[] {
  return PAGE_CATALOG[getBrandForTenant(tenantSlug)];
}

/** Looks up one page in a tenant's catalog. Returns `undefined` for a slug outside it. */
export function findCatalogPage(
  tenantSlug: string | null | undefined,
  slug: string,
): CatalogPage | undefined {
  return getPagesForTenant(tenantSlug).find(page => page.slug === slug);
}
